import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/apiConfig';
import { renderField } from './fieldRenderers';
import { mutate } from 'swr';
import { useDynamicLookups } from '../../hooks/useDynamicLookups';
import { useCreateEntity, useUpdateEntity } from '../../hooks/useSWR';
import { useResponsiveWidth } from '../../hooks/useWindowSize';
import { XIcon, SpinnerIcon } from '../ui/Icons';

/**
 * Generic Modal Form Component
 * Follows the same pattern as FormDrawer and other generic components
 * Uses fieldRenderers for consistent field rendering
 */
const ModalForm = ({
  isOpen,
  onClose,
  config,
  initialData = null,
  mode = 'create', // 'create' or 'edit'
  onSuccess,
  parentId = null, // For nested entities (e.g., capaId for actions)
  parentEntity = null, // For nested entities with type info (e.g., RiskAssessment for risk mitigation actions)
  customHandlers = {}
}) => {
  const { theme } = useTheme();
  
  // Get responsive width based on screen size
  const responsiveWidth = useResponsiveWidth(config.ui?.modalWidth || '600px');
  
  // Fetch lookup data dynamically based on config
  const lookupEndpoints = useMemo(() => config.api?.lookupEndpoints || {}, [config.api?.lookupEndpoints]);
  const memoizedLookupQueries = useDynamicLookups(lookupEndpoints);
  
  // SWR hooks for API operations
  const createEntity = useCreateEntity(config.api?.baseEndpoint || '');
  const updateEntity = useUpdateEntity(config.api?.baseEndpoint || '');
  
  // State management (following the same pattern as FormDrawer)
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formNotifications, setFormNotifications] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    console.log('ModalForm useEffect - isOpen:', isOpen, 'initialData:', initialData);
    if (isOpen) {
      console.log('ModalForm - Initial Data JSON:', JSON.stringify(initialData, null, 2));
      setFormData(initialData || {});
      setFormErrors({});
      setFormNotifications([]);
      setIsFormValid(false);
    }
  }, [isOpen, initialData]);

  // Validate form when formData changes
  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation - check required fields
    config.fields.forEach(field => {
      if (field.validation?.required && (!formData[field.name] || formData[field.name] === '')) {
        newErrors[field.name] = field.validation?.message || `${field.label} is required`;
      }
    });

    setFormErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  const validateField = (fieldName, value) => {
    const field = config.fields.find(f => f.name === fieldName);
    if (!field || !field.validation) return null;

    const { validation } = field;
    
    if (validation.required && (!value || value === '')) {
      return validation.message || `${field.label} is required`;
    }

    if (validation.maxLength && value && value.length > validation.maxLength) {
      return validation.message || `${field.label} must not exceed ${validation.maxLength} characters`;
    }

    if (validation.min && value && parseFloat(value) < validation.min) {
      return validation.message || `${field.label} must be at least ${validation.min}`;
    }

    if (validation.max && value && parseFloat(value) > validation.max) {
      return validation.message || `${field.label} must not exceed ${validation.max}`;
    }

    return null;
  };

  const clearFormNotifications = () => {
    setFormNotifications([]);
  };

  const addFormNotification = (message, type = 'info') => {
    setFormNotifications(prev => [...prev, { message, type }]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Note: We allow submission even if form is invalid - validation will show errors
    // but user can still attempt to save and see backend validation errors

    setIsLoading(true);
    clearFormNotifications();

    try {
      // Transform data using config function if available
      let transformedData = config.transformData ? config.transformData(formData, mode) : formData;
      
      // Filter out fields that should not be sent to the backend
      if (config.fieldsToRemove && Array.isArray(config.fieldsToRemove)) {
        const filteredData = {};
        Object.keys(transformedData).forEach(key => {
          if (!config.fieldsToRemove.includes(key)) {
            filteredData[key] = transformedData[key];
          }
        });
        transformedData = filteredData;
      }
      
      
      // Add parent ID if provided (for nested entities)
      if (parentId) {
        // Determine the parent ID field name based on the entityTypeId
        const entityTypeId = config.entityTypeId;
        if (entityTypeId === 1) {
          // Documents
          transformedData.documentId = parentId;
        } else if (entityTypeId === 3) {
          // CAPA
          transformedData.capaId = parentId;
        } else if (entityTypeId === 4) {
          // Risk Assessment
          transformedData.riskAssessmentId = parentId;
        } else if (entityTypeId === 5) {
          // Non-Conformity Report
          transformedData.ncrId = parentId;
        } else if (entityTypeId === 6) {
          // Change Request
          transformedData.changeRequestId = parentId;
        } else if (entityTypeId === 7) {
          // Complaint
          transformedData.complaintId = parentId;
        } else {
          // Default fallback - try to infer from config or use generic parentId
          const parentFieldName = config.parentFieldName || 'parentId';
          transformedData[parentFieldName] = parentId;
        }
      }
      

      // Use custom onSave handler if provided (same as GenericFormsPage)
      let result;
      try {
        if (config.onSave) {
          result = await config.onSave(transformedData, mode, null, null);
        } else {
          // Use SWR hooks for API operations
          if (mode === 'create') {
            result = await createEntity(transformedData, config.fieldsToRemove || []);
          } else {
            result = await updateEntity(initialData?.id, transformedData, config.fieldsToRemove || []);
          }
        }
      } catch (saveError) {
        // Reset loading immediately on save error
        setIsLoading(false);
        throw saveError; // Re-throw to be caught by outer catch block
      }
      
      // Show success message
      const successMessage = mode === 'create' 
        ? config.messages?.createSuccess || `${config.entityName} created successfully!`
        : config.messages?.updateSuccess || `${config.entityName} updated successfully!`;
      
      addFormNotification(successMessage, 'success');

      // Invalidate SWR cache (same as GenericFormsPage)
      if (config.api?.swrInvalidation) {
        if (config.api.swrInvalidation.mainList) {
          mutate(config.api.swrInvalidation.mainList);
        }
        if (config.api.swrInvalidation.withFileAttachments) {
          mutate(config.api.swrInvalidation.withFileAttachments);
        }
        if (config.api.swrInvalidation.fileAttachments && config.entityTypeId) {
          const fileAttachmentsEndpoint = config.api.swrInvalidation.fileAttachments(config.entityTypeId);
          mutate(fileAttachmentsEndpoint);
        }
      } else if (config.apiEndpoint) {
        mutate(config.apiEndpoint);
      }

      // Call custom success handler
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
     
      // Enhanced error handling (same as GenericFormsPage)
      const payload = error?.info;
      let msg = error?.message || '';
      
      if (Array.isArray(payload?.details) && payload.details.length) {
        msg = payload.details.join(', ');
      }
      
      if (payload?.errorCode === 'P2002') {
        const tgt = Array.isArray(payload?.meta?.target)
          ? payload.meta.target.join(', ')
          : payload?.meta?.target;
        msg = `Unique constraint violation${tgt ? ` (${tgt})` : ''}`;
      }
      
      if (payload?.errorCode === 'P2003') {
        const constraint = payload?.meta?.constraint;
        const model = payload?.meta?.modelName;
        msg = `Cannot ${mode} ${model || config.entityName}: it is referenced by related records${constraint ? ` (${constraint})` : ''}.`;
      }

      const errorMessage = msg || (mode === 'create' 
        ? config.messages?.createError || `Failed to create ${config.entityName}`
        : config.messages?.updateError || `Failed to update ${config.entityName}`);

      addFormNotification(errorMessage, 'error');
      // Ensure loading state is reset on error so user can try again
      setIsLoading(false);
    } finally {
      // Double-check that loading is always reset
      setIsLoading(false);
    }
  };

  const renderFormField = (field) => {
    // Extract value - handle nested objects for display fields
    let value = formData[field.name] || '';
    
    // If field has a displayField mapping and value is an object, extract the display value
    if (config.fieldMappings && config.fieldMappings[field.name]) {
      const mappingPath = config.fieldMappings[field.name];
      const pathParts = mappingPath.split('.');
      if (pathParts.length > 1 && typeof value === 'object' && value !== null) {
        // Navigate the object path (e.g., 'currentRevision.version')
        let nestedValue = value;
        for (const part of pathParts.slice(1)) {
          nestedValue = nestedValue?.[part];
        }
        if (nestedValue !== undefined && nestedValue !== null) {
          value = nestedValue;
        }
      }
    }
    
    console.log(`ModalForm renderFormField - field: ${field.name}, value:`, value, 'formData:', formData);
    
    // Enhance field with lookup options if it has a lookupEndpoint
    let enhancedField = { ...field };
    if (field.lookupEndpoint && (field.type === 'select' || field.type === 'multiSelect')) {
      const lookupEndpoint = field.lookupEndpoint;
      const rawLookupData = memoizedLookupQueries[lookupEndpoint] || [];
      const lookupData =
        typeof field.lookupFilter === 'function'
          ? rawLookupData.filter((item) => field.lookupFilter(item))
          : rawLookupData;
      
      // Convert lookup data to options format
      const options = Array.isArray(lookupData) && lookupData.length > 0 
        ? lookupData.map(item => {
            if (!item || typeof item !== 'object' || item.id == null) {
              return null;
            }

            // Use custom option label formatter first, then labelField, then fallbacks
            let labelValue;
            if (typeof field.optionLabel === 'function') {
              labelValue = field.optionLabel(item);
            } else if (field.labelField && item[field.labelField] && String(item[field.labelField]).trim()) {
              labelValue = item[field.labelField];
            } else {
              labelValue =
                item.name ||
                item.catName ||
                item.typeName ||
                item.statusName ||
                item.title ||
                item.userDisplayName ||
                `Item ${item.id}`;
            }
            
            return {
              value: item.id,
              label: String(labelValue)
            };
          }).filter(Boolean)
        : [];
      
      enhancedField = {
        ...field,
        options
      };
    }
    
    return renderField(enhancedField, {
      value,
      formData,
      setFormData,
      formErrors,
      setFormErrors,
      validateField,
      labels: {
        selectPrefix: 'Select',
        enterPrefix: 'Enter',
        allPrefix: 'All',
        choosePrefix: 'Choose',
        ...config?.labels
      },
      config,
      setFilesToDelete: () => {} // Not used in modal form
    });
  };

  const renderFormSections = () => {
    console.log('ModalForm renderFormSections - config:', config);
    console.log('ModalForm renderFormSections - config.fields:', config?.fields);
    
    // Use the same section logic as FormDrawer
    const defaultPreset = config.formLayoutPresets?.default || 'standard';
    const currentPreset = config.formLayoutPresets?.[defaultPreset];
    const sectionsToUse = currentPreset?.useSections === false 
      ? currentPreset.formSections 
      : config.formSections;
    
    if (!sectionsToUse) {
      // Fallback: render all fields vertically
      const fieldsToRemove = config.fieldsToRemove || [];
      return (
        <div className="space-y-6">
          {config?.fields?.filter(field => 
            field.type !== 'hidden' && 
            !fieldsToRemove.includes(field.name) &&
            !['createdAt', 'createdBy', 'updatedAt', 'updatedBy'].includes(field.name)
          ).map(renderFormField) || <div>No fields configured</div>}
        </div>
      );
    }

    return sectionsToUse.map((section, sectionIndex) => (
      <div key={sectionIndex} className="space-y-4 mb-8">
        {/* Custom sections usually own their title; opt in with showSectionHeader */}
        {(section.layout !== 'custom' || section.showSectionHeader) && (
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-2">
            {section.icon && <span>{section.icon}</span>}
            {section.title}
          </h3>
        )}
        <div className="space-y-6">
          {section.layout === 'grid' ? (
            // Grid layout for specific sections
            <div className={`grid grid-cols-1 ${section.gridCols || 'md:grid-cols-2'} gap-4`}>
              {section.fields.map(fieldName => {
                const field = config.fields.find(f => f.name === fieldName);
                return field ? (
                  <div key={fieldName} className={section.fieldSpans?.[fieldName] || ''}>
                    {renderFormField(field)}
                  </div>
                ) : null;
              }).filter(Boolean)}
            </div>
          ) : (
            // Vertical layout
            <div className="space-y-6">
              {section.fields.map(fieldName => {
                const field = config.fields.find(f => f.name === fieldName);
                return field ? renderFormField(field) : null;
              }).filter(Boolean)}
            </div>
          )}
        </div>
      </div>
    ));
  };

  if (!isOpen) return null;

  const modalTitle = mode === 'create' 
    ? config.labels?.addNew || `Add New ${config.labels?.singular || config.entityName}`
    : config.labels?.editAction || `Edit ${config.labels?.singular || config.entityName}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={cn(
          "relative bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden flex flex-col",
          "mx-4 transition-all duration-300"
        )}
        style={{ 
          width: responsiveWidth,
          maxWidth: '95vw', // Ensure it never exceeds viewport width
          height: config.ui?.modalHeight || '600px',
          maxHeight: config.ui?.modalMaxHeight || '90vh'
        }}
      >
        {/* Header */}
        <div className={cn("px-6 py-4 border-b flex-shrink-0", theme.border.light)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("text-lg font-semibold", theme.text.primary)}>
                {modalTitle}
              </div>
            </div>
            <button
              onClick={onClose}
              className={cn("p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors", theme.text.secondary)}
              disabled={isLoading}
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form Notifications */}
        {formNotifications.length > 0 && (
          <div className="px-6 py-3 flex-shrink-0">
            {formNotifications.map((notification, index) => (
              <div
                key={index}
                className={cn(
                  "p-3 rounded-lg mb-2",
                  notification.type === 'error' 
                    ? "bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
                    : notification.type === 'success'
                    ? "bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400"
                    : "bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400"
                )}
              >
                {notification.message}
              </div>
            ))}
          </div>
        )}

        {/* Form Content with Scrollbar */}
        <div className="flex-1 overflow-y-auto modal-form-scroll">
          <div className="p-6 pb-12">
            <form onSubmit={handleFormSubmit}>
              {renderFormSections()}
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className={cn("px-6 py-4 border-t flex items-center justify-end gap-3 flex-shrink-0", theme.border.light)}>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={cn(
              "px-4 py-2 rounded-lg border transition-colors",
              theme.border.light,
              theme.text.secondary,
              "hover:bg-gray-50 dark:hover:bg-gray-700",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "px-4 py-2 rounded-lg transition-colors",
              theme.colors.primary,
              theme.text.inverse,
              "hover:opacity-90",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            onClick={handleFormSubmit}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <SpinnerIcon className="w-4 h-4" />
                {mode === 'create' ? 'Creating...' : 'Updating...'}
              </div>
            ) : (
              mode === 'create' ? 'Create' : 'Update'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalForm;
